import type { StripSubject } from '@/components/organisms/PosterStrip';

/**
 * Worked examples — one real, already-bound subject per template.
 *
 * A template asks "what do you want to analyse?", which only helps someone who
 * already knows what the product can do. A worked example answers instead: it
 * is the same analysis already run on a recognisable subject, so the tile shows
 * the output rather than asking for input. Opening one lands on rendered
 * figures; changing the subject re-runs it. That is the adoption path, and it
 * is also the clearest possible statement of what a workflow *is* — a structure
 * you run against a subject.
 *
 * Every template has one. The first entry for a template is its face on the
 * home page; any others become alternate subjects offered on the same tile.
 *
 * Nothing here is aspirational — a tile that can't render is worse than no
 * tile. Every subject was checked against the live leaderboard and every poster
 * and logo URL confirmed to resolve (2026-08-17).
 */
export interface WorkedExample {
  id: string;
  /** Template this instantiates. */
  templateId: string;
  /** The subject, as shown on the tile. */
  name: string;
  /** The analysis being run on it. */
  analysis: string;
  /** What the reader learns — one line. Used by the canvas, not the tile. */
  blurb: string;
  /** Pre-bound template inputs. */
  inputs: Record<string, string>;
  /** Artwork for the tile. */
  subject: StripSubject;
}

export const WORKED_EXAMPLES: WorkedExample[] = [
  // ─── Content Valuation ─────────────────────────────────────────────────────

  {
    id: 'example-stranger-things',
    templateId: 'title-valuation',
    name: 'Stranger Things',
    analysis: 'Title Valuation',
    blurb:
      'What Netflix’s flagship is worth: revenue to date, the split between winning and keeping subscribers, and where in the world it earns.',
    inputs: { titleId: '065ecb7f-5bef-4001-aba9-20900708af22' },
    subject: { kind: 'titles', ids: ['065ecb7f-5bef-4001-aba9-20900708af22'] },
  },
  {
    id: 'example-bluey',
    templateId: 'title-valuation',
    name: 'Bluey',
    analysis: 'Title Valuation',
    blurb:
      'A kids’ IP that travels: how an Australian show earns across four regions, and how much of it is retention rather than acquisition.',
    inputs: { titleId: '9484dca7-69d0-4da5-a259-838e06842e45' },
    subject: { kind: 'titles', ids: ['9484dca7-69d0-4da5-a259-838e06842e45'] },
  },

  {
    /* Carried on five platforms — the widest spread in the catalogue, which is
       what makes it the right subject for a "which platform suits it" read. */
    id: 'example-the-rookie',
    templateId: 'platform-fit',
    name: 'The Rookie',
    analysis: 'Platform Fit',
    blurb:
      'A procedural carried on five platforms at once — where the same show earns most, and whether it wins subscribers or keeps them.',
    inputs: { titleId: '820ff020-67d7-4d9e-95f2-c952e57e5455' },
    subject: { kind: 'titles', ids: ['820ff020-67d7-4d9e-95f2-c952e57e5455'] },
  },

  {
    id: 'example-harry-potter',
    templateId: 'franchise-assessment',
    name: 'Harry Potter',
    analysis: 'Franchise Assessment',
    blurb:
      'Four films valued as one asset — which instalment carries the franchise, and how its value spreads across platforms and regions.',
    inputs: { search: 'Harry Potter' },
    subject: {
      kind: 'titles',
      ids: [
        '20f4ef10-901e-47e1-8534-40bd3b5a6569', // Goblet of Fire
        '351b3c49-f2d0-4973-9e04-b5706f2b2fb9', // Prisoner of Azkaban
        '96727c0a-cbad-41a5-97fc-9d7f36fef8b5', // Sorcerer's Stone
        '14185530-72bc-4f95-bad1-380d3bd00696', // Chamber of Secrets
      ],
    },
  },
  {
    id: 'example-star-trek',
    templateId: 'franchise-assessment',
    name: 'Star Trek',
    analysis: 'Franchise Assessment',
    blurb:
      'Nine series and $2B of catalogue value — how a decades-old franchise still earns, and which era does the heavy lifting.',
    inputs: { search: 'Star Trek' },
    subject: {
      kind: 'titles',
      ids: [
        'MVe28f7551-55a4-420a-b745-dc6efc9af530', // The Next Generation
        '489117df-a37e-4a02-a1ac-8f413f3a197d', // Deep Space Nine
        'efc1e878-bce3-42d2-ac7a-c173e41d9413', // Voyager
        '2e7db0bf-ee84-4129-8e01-403e36c09334', // Enterprise
      ],
    },
  },

  {
    id: 'example-disney-catalogue',
    templateId: 'catalogue-review',
    name: 'Disney+',
    analysis: 'Catalogue Review',
    blurb:
      'What actually carries a franchise-heavy catalogue: the highest-earning titles, the genre mix behind them, and how far revenue concentrates at the top.',
    inputs: { platform: 'Disney+' },
    subject: { kind: 'platforms', names: ['Disney+'] },
  },

  {
    id: 'example-max-genres',
    templateId: 'genre-performance',
    name: 'Max',
    analysis: 'Genre Performance',
    blurb:
      'Which genres actually earn on a prestige-drama service — revenue split by genre, with the titles driving each one.',
    inputs: { platform: 'Max' },
    subject: { kind: 'platforms', names: ['Max'] },
  },

  // ─── Streaming Metrics ─────────────────────────────────────────────────────

  {
    id: 'example-netflix',
    templateId: 'platform-scorecard',
    name: 'Netflix',
    analysis: 'Platform Scorecard',
    blurb:
      'Subscribers, ARPU, revenue and churn for the quarter, with the full subscriber history behind them.',
    inputs: { platform: 'Netflix' },
    subject: { kind: 'platforms', names: ['Netflix'] },
  },

  {
    id: 'example-market',
    templateId: 'market-leaderboard',
    name: 'The streaming market',
    analysis: 'Market Leaderboard',
    blurb:
      'Every major streamer ranked on subscribers, ARPU, revenue and churn — including the ones that never disclose their numbers.',
    inputs: {},
    subject: {
      kind: 'platforms',
      names: ['Netflix', 'Amazon Prime Video', 'Disney+', 'Max', 'Hulu', 'Apple TV+'],
    },
  },

  {
    /* Runs as-is like Market Leaderboard, so its face leads with the platforms
       that actually leak — otherwise the two tiles would be the same picture. */
    id: 'example-churn',
    templateId: 'churn-analysis',
    name: 'Where subscribers leak',
    analysis: 'Churn Analysis',
    blurb:
      'The leakiest services in the market: gross additions set against churn, and what each one is left with in net terms.',
    inputs: {},
    subject: {
      kind: 'platforms',
      names: ['Peacock', 'Discovery+', 'Apple TV+', 'STARZ', 'Paramount+'],
    },
  },

  {
    /* The steepest curve on record — the clearest read of the template. */
    id: 'example-apple-growth',
    templateId: 'growth-trajectory',
    name: 'Apple TV+',
    analysis: 'Growth Trajectory',
    blurb:
      'The steepest growth curve on record — subscribers, net additions and ARPU for every quarter since launch.',
    inputs: { platform: 'Apple TV+' },
    subject: { kind: 'platforms', names: ['Apple TV+'] },
  },

  {
    id: 'example-prime-regions',
    templateId: 'regional-breakdown',
    name: 'Amazon Prime Video',
    analysis: 'Regional Breakdown',
    blurb:
      'How a global service performs market by market — subscribers, revenue and ARPU split across every region it operates in.',
    inputs: { platform: 'Amazon Prime Video' },
    subject: { kind: 'platforms', names: ['Amazon Prime Video'] },
  },
];

/** The example that becomes a template's face, plus any alternate subjects. */
export function examplesForTemplate(templateId: string): WorkedExample[] {
  return WORKED_EXAMPLES.filter((e) => e.templateId === templateId);
}

export function findExample(id: string | undefined): WorkedExample | undefined {
  return id ? WORKED_EXAMPLES.find((e) => e.id === id) : undefined;
}
